import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid } from '../cacheUtils';

/**
 * Fetch bundle packaging data (plans, features, add-ons, coupons, promo codes)
 */
export const fetchBundlePackaging = createAsyncThunk(
  'bundlePackaging/fetchData',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().bundlePackaging;
    
    if (isCacheValid(state.lastFetched)) {
      return state.data;
    }

    try {
      const response = await Axios.get('/api/dashboard/bundle-packaging');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bundle packaging data');
    }
  }
);

/**
 * Fetch available features
 */
export const fetchFeatures = createAsyncThunk(
  'bundlePackaging/fetchFeatures',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().bundlePackaging;
    
    if (isCacheValid(state.featuresLastFetched)) {
      return state.features;
    }

    try {
      const response = await Axios.get('/api/key/features');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch features');
    }
  }
);

/**
 * Create a new plan
 */
export const createPlan = createAsyncThunk(
  'bundlePackaging/createPlan',
  async (planData, { rejectWithValue }) => {
    try {
      const response = await Axios.post('/api/dashboard/bundle-packaging/plans', planData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create plan');
    }
  }
);

/**
 * Update an existing plan
 */
export const updatePlan = createAsyncThunk(
  'bundlePackaging/updatePlan',
  async ({ planId, planData }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/api/dashboard/bundle-packaging/plans/${planId}`, planData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update plan');
    }
  }
);

/**
 * Delete a plan
 */
export const deletePlan = createAsyncThunk(
  'bundlePackaging/deletePlan',
  async (planId, { rejectWithValue }) => {
    try {
      await Axios.delete(`/api/dashboard/bundle-packaging/plans/${planId}`);
      return planId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete plan');
    }
  }
);

/**
 * Create a new feature
 */
export const createFeature = createAsyncThunk(
  'bundlePackaging/createFeature',
  async (featureData, { rejectWithValue }) => {
    try {
      const response = await Axios.post('/api/key/features', featureData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create feature');
    }
  }
);

/**
 * Update an existing feature
 */
export const updateFeature = createAsyncThunk(
  'bundlePackaging/updateFeature',
  async ({ featureKey, featureData }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/api/key/features/${featureKey}`, featureData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update feature');
    }
  }
);

/**
 * Delete a feature
 */
export const deleteFeature = createAsyncThunk(
  'bundlePackaging/deleteFeature',
  async (featureKey, { rejectWithValue }) => {
    try {
      await Axios.delete(`/api/key/features/${featureKey}`);
      return featureKey;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete feature');
    }
  }
);

/**
 * Create a new add-on
 */
export const createAddOn = createAsyncThunk(
  'bundlePackaging/createAddOn',
  async (addOnData, { rejectWithValue }) => {
    try {
      const response = await Axios.post('/api/dashboard/bundle-packaging/addons', addOnData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create add-on');
    }
  }
);

/**
 * Update an existing add-on
 */
export const updateAddOn = createAsyncThunk(
  'bundlePackaging/updateAddOn',
  async ({ addOnId, addOnData }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/api/dashboard/bundle-packaging/addons/${addOnId}`, addOnData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update add-on');
    }
  }
);

/**
 * Delete an add-on
 */
export const deleteAddOn = createAsyncThunk(
  'bundlePackaging/deleteAddOn',
  async (addOnId, { rejectWithValue }) => {
    try {
      await Axios.delete(`/api/dashboard/bundle-packaging/addons/${addOnId}`);
      return addOnId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete add-on');
    }
  }
);

const bundlePackagingSlice = createSlice({
  name: 'bundlePackaging',
  initialState: {
    data: null,
    features: [],
    loading: false,
    featuresLoading: false,
    mutationLoading: false,
    error: null,
    lastFetched: null,
    featuresLastFetched: null,
  },
  reducers: {
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.featuresLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Bundle Packaging
    builder.addCase(fetchBundlePackaging.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBundlePackaging.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchBundlePackaging.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Features
    builder.addCase(fetchFeatures.pending, (state) => {
      state.featuresLoading = true;
      state.error = null;
    });
    builder.addCase(fetchFeatures.fulfilled, (state, action) => {
      state.featuresLoading = false;
      state.features = action.payload;
      state.featuresLastFetched = Date.now();
    });
    builder.addCase(fetchFeatures.rejected, (state, action) => {
      state.featuresLoading = false;
      state.error = action.payload;
    });

    // Create Plan
    builder.addCase(createPlan.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(createPlan.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(createPlan.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Update Plan
    builder.addCase(updatePlan.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(updatePlan.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(updatePlan.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Delete Plan
    builder.addCase(deletePlan.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(deletePlan.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(deletePlan.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Create Feature
    builder.addCase(createFeature.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(createFeature.fulfilled, (state) => {
      state.mutationLoading = false;
      state.featuresLastFetched = null; // Invalidate cache
    });
    builder.addCase(createFeature.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Update Feature
    builder.addCase(updateFeature.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(updateFeature.fulfilled, (state) => {
      state.mutationLoading = false;
      state.featuresLastFetched = null; // Invalidate cache
    });
    builder.addCase(updateFeature.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Delete Feature
    builder.addCase(deleteFeature.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(deleteFeature.fulfilled, (state) => {
      state.mutationLoading = false;
      state.featuresLastFetched = null; // Invalidate cache
    });
    builder.addCase(deleteFeature.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Create Add-on
    builder.addCase(createAddOn.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(createAddOn.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(createAddOn.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Update Add-on
    builder.addCase(updateAddOn.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(updateAddOn.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(updateAddOn.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });

    // Delete Add-on
    builder.addCase(deleteAddOn.pending, (state) => {
      state.mutationLoading = true;
      state.error = null;
    });
    builder.addCase(deleteAddOn.fulfilled, (state) => {
      state.mutationLoading = false;
      state.lastFetched = null; // Invalidate cache
    });
    builder.addCase(deleteAddOn.rejected, (state, action) => {
      state.mutationLoading = false;
      state.error = action.payload;
    });
  },
});

export const { invalidateCache: invalidateBundlePackagingCache } = bundlePackagingSlice.actions;
export default bundlePackagingSlice.reducer;